import { useCallback, useEffect, useState } from 'react'
import type { ArchiveGroup, QueueSort } from '@/lib/audition/queue'
import { type RouterInputs, trpc } from '@/lib/trpc'

/**
 * One place that knows what a decision invalidates (LG-118 Slice 5).
 *
 * Every action here changes the submission's stage, its credits, or its
 * cooldown — all of which the detail query returns — so a stale detail view
 * after a decision is the failure mode to design against. The list and the
 * lane mix move too (stage counts), and so does the home-card badge, which is
 * the one an admin sees from a page they never reloaded.
 */
export function useAuditionInvalidation(submissionId: string) {
  const utils = trpc.useUtils()
  // Returns the promise: discarding it flipped `isPending` false before the
  // refetch landed, so buttons re-enabled over stale data.
  return () =>
    Promise.all([
      utils.admin.auditions.detail.invalidate({ submissionId }),
      utils.admin.auditions.list.invalidate(),
      // The archives move too: a decision is exactly what puts somebody into
      // one, and the group they left is a different query from the one they
      // arrived in.
      utils.admin.auditions.browse.invalidate(),
      utils.admin.auditions.laneMix.invalidate(),
      utils.admin.auditions.queueSummary.invalidate(),
    ])
}

/**
 * Which selection criterion the reviewer is currently judging on (LG-414).
 *
 * Level vs motivation is genuinely undecided by the founders, so the page
 * supports both and remembers the choice: the lens is picked on the queue and
 * has to still be in force when the reviewer opens an application, which is a
 * different route.
 *
 * `localStorage` rather than a context provider because the two screens do not
 * share a mount — a provider would have to wrap the whole admin app to carry
 * one enum across a navigation. Reads are guarded: `localStorage` throws in
 * private-mode Safari, and a preference is never worth a blank page.
 */
export type ReviewLens = 'level' | 'motivation'

const LENS_KEY = 'admin.auditions.lens'

function readLens(): ReviewLens {
  try {
    return globalThis.localStorage?.getItem(LENS_KEY) === 'motivation'
      ? 'motivation'
      : 'level'
  } catch {
    return 'level'
  }
}

export function useReviewLens(): [ReviewLens, (lens: ReviewLens) => void] {
  const [lens, setLensState] = useState<ReviewLens>(readLens)
  const setLens = useCallback((next: ReviewLens) => {
    setLensState(next)
    try {
      globalThis.localStorage?.setItem(LENS_KEY, next)
    } catch {
      // Preference lost on reload; the page still works. Nothing to report.
    }
  }, [])
  return [lens, setLens]
}

/**
 * A value that lags its input until the typing stops (LG-914).
 *
 * `useDeferredValue` is not this: it only skips an intermediate value when a
 * render is still in flight when the next one arrives, so on a component that
 * commits every keystroke it takes every prefix — and every prefix of an
 * archive search is a fresh query key for three infinite queries plus three
 * `count()`s over the table this ticket exists to stop scanning. Typing a name
 * would issue thirty of them.
 *
 * A value hook rather than the `useAutoSave` machinery, which debounces a
 * SAVE and owns retry and dirty state; there is nothing to persist here.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): T {
  // Stored lazily and set functionally so the signature is not a lie: passed
  // through `useState`'s plain channel, a `T` that is itself a function would
  // be taken for an updater and the hook would hold `value(previous)` — the
  // one shape the type checker cannot see going wrong.
  const [settled, setSettled] = useState<T>(() => value)
  useEffect(() => {
    const timer = setTimeout(() => setSettled(() => value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])
  return settled
}

/**
 * One archive group, a page at a time (LG-914).
 *
 * The three archive groups are the half of the table that grows with
 * marketing traffic rather than with hiring, so the queue reads them only
 * when an admin is actually looking at one — `enabled` is the whole point of
 * this hook, not a detail of it.
 *
 * `total` is the SQL count for the current filter and comes back on the first
 * page only; the pages after it carry `null` rather than re-counting a table
 * nobody should be scanning once per "Show more". Reading it off page zero is
 * what keeps the heading honest for the whole scroll.
 */
export function useArchiveGroup({
  group,
  enabled,
  sort,
  search,
  lane,
  paidOnly,
}: {
  group: ArchiveGroup
  enabled: boolean
  sort: QueueSort
  search: string
  // Taken from the procedure rather than respelled: the lane list is owned by
  // the db enum, and a third hand-written copy of it is the one that drifts.
  lane: NonNullable<RouterInputs['admin']['auditions']['browse']['lane']> | null
  paidOnly: boolean
}) {
  const query = trpc.admin.auditions.browse.useInfiniteQuery(
    {
      group,
      sort,
      q: search || undefined,
      lane: lane ?? undefined,
      paidOnly: paidOnly || undefined,
    },
    {
      enabled,
      getNextPageParam: (page) => page.nextCursor ?? undefined,
      // Same reason as the queue's own list: changing the sort or the search
      // asks for an uncached key, and without this the group collapses to a
      // spinner under the admin's cursor mid-scroll.
      placeholderData: (previous) => previous,
    },
  )

  // Everything derived is gated on `enabled`, not just `isPending`: react-query
  // keeps serving a disabled query's cached data, so a group the admin has
  // filtered away would otherwise still be handing out rows and a count that
  // no longer render anywhere.
  const pages = enabled ? (query.data?.pages ?? []) : []
  return {
    rows: pages.flatMap((page) => page.rows),
    // How many pages have been asked for, which is the only honest way to know
    // the group ever offered a "Show more" — the page size lives on the server
    // and comparing row counts against a copy of it here is the drift this
    // module already avoids everywhere else.
    pageCount: pages.length,
    // By its marker, not its position: `browse` puts the count on the
    // cursorless page and `null` on the rest.
    total: pages.find((page) => page.total !== null)?.total ?? null,
    isPending: enabled && query.isPending,
    isError: enabled && query.isError,
    // The other half of `placeholderData`. Without it the previous search's
    // rows and the previous search's count read as the answer to the current
    // one, with nothing on screen marked busy.
    isPlaceholderData: enabled && query.isPlaceholderData,
    hasNextPage: enabled && query.hasNextPage,
    isFetchingNextPage: enabled && query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    // A force-open group renders no Hide/Show toggle, so without this a failed
    // archive under a search has no way back short of a page reload.
    refetch: query.refetch,
  }
}
